import React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import type {
  IContact,
  NewContactForm,
  VenueData,
  VenueContact,
} from "../../types/venues.types";
import { SEARCH_MIN_CHARS, SEARCH_DEBOUNCE_MS } from "../utils/venue.utils";

interface UseContactSearchProps {
  venueId: string | undefined;
  venueData: VenueData | null;
  setVenueData: React.Dispatch<React.SetStateAction<VenueData | null>>;
  apiService: {
    get: <T>(url: string) => Promise<{ data: T }>;
    post: <T>(url: string, data: object) => Promise<{ data: T }>;
    patch: <T>(url: string, data: object) => Promise<{ data: T }>;
  };
}

interface UseContactSearchReturn {
  openContactModal: boolean;
  setOpenContactModal: (open: boolean) => void;
  contactSearch: string;
  setContactSearch: (search: string) => void;
  searchResults: IContact[];
  isSearching: boolean;
  savingContact: boolean;
  contactModalStep: "search" | "form";
  setContactModalStep: (step: "search" | "form") => void;
  searchAttempted: boolean;
  newContactForm: NewContactForm;
  setNewContactForm: (form: NewContactForm) => void;
  handleAddExistingContact: (contact: IContact) => Promise<void>;
  handleAddNewContact: () => Promise<void>;
  resetContactModal: () => void;
}

export const useContactSearch = ({
  venueId,
  venueData,
  setVenueData,
  apiService,
}: UseContactSearchProps): UseContactSearchReturn => {
  const [openContactModal, setOpenContactModal] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [searchResults, setSearchResults] = useState<IContact[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [contactModalStep, setContactModalStep] = useState<"search" | "form">(
    "search",
  );
  const [searchAttempted, setSearchAttempted] = useState(false);
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [newContactForm, setNewContactForm] = useState<NewContactForm>({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    role: "",
  });

  const searchContactsAPI = useCallback(
    async (term: string) => {
      if (term.trim().length < SEARCH_MIN_CHARS) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const res = await apiService.get<{ results: IContact[] }>(
          `/api/contacts/contact/?contact_type=individual&search=${encodeURIComponent(
            term.trim(),
          )}`,
        );
        setSearchResults(res.data.results || []);
      } catch (error) {
        console.error("Search error:", error);
        toast.error("Failed to search contacts");
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [apiService],
  );

  useEffect(() => {
    setSearchAttempted(contactSearch.length > 0);

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    if (contactSearch.trim().length > 0) {
      searchTimerRef.current = setTimeout(() => {
        searchContactsAPI(contactSearch);
      }, SEARCH_DEBOUNCE_MS);
    } else {
      setSearchResults([]);
    }

    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, [contactSearch, searchContactsAPI]);

  const handleAddExistingContact = async (contact: IContact) => {
    setSavingContact(true);
    try {
      const existingContactIds = (venueData?.contacts ?? []).map((c) => c.id);

      await apiService.patch(`/api/venues/venue/${venueId}/add-contacts/`, {
        contact_ids: [...existingContactIds, contact.id],
      });

      if (venueData) {
        const newContact: VenueContact = {
          id: Number(contact.id),
          first_name: contact.first_name || "",
          last_name: contact.last_name || "",
          email: contact.email || "",
          phone: contact.phone || "",
          contact_image: contact.contact_image || "",
          twitter_handle: null,
          instagram_handle: null,
          facebook_handle: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          role: contact.role ?? null,
          venue: Number.parseInt(venueId || "0"),
        };

        setVenueData({
          ...venueData,
          contacts: [...venueData.contacts, newContact],
        });
      }

      toast.success("Contact added to venue successfully!");
      resetContactModal();
    } catch (error) {
      const errorMessage =
        (error as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Failed to add contact to venue";
      toast.error(errorMessage);
    } finally {
      setSavingContact(false);
    }
  };

  const handleAddNewContact = async () => {
    if (!newContactForm.first_name.trim() || !newContactForm.last_name.trim()) {
      toast.error("Please fill in First Name and Last Name");
      return;
    }
    if (!newContactForm.email.trim() && !newContactForm.phone?.trim()) {
      toast.error("Please provide an email or a phone number");
      return;
    }

    setSavingContact(true);
    try {
      const contactRes = await apiService.post<IContact>(
        "/api/contacts/contact/",
        newContactForm,
      );
      const newContact = contactRes.data;
      toast.success("Contact created successfully!");

      const existingContactIds = (venueData?.contacts ?? []).map((c) => c.id);

      await apiService.patch(`/api/venues/venue/${venueId}/add-contacts/`, {
        contact_ids: [...existingContactIds, newContact.id],
      });

      toast.success("Contact added to venue successfully!");

      if (venueData) {
        const contactId = Number(newContact.id);
        const normalizedContact: VenueContact = {
          id: Number.isNaN(contactId) ? 0 : contactId,
          first_name: newContact.first_name || "",
          last_name: newContact.last_name || "",
          contact_image: newContact.contact_image || "",
          email: newContact.email || "",
          phone: newContact.phone || "",
          twitter_handle: null,
          instagram_handle: null,
          facebook_handle: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          role: newContact.role ?? newContactForm.role ?? null,
          venue: Number.parseInt(venueId || "0"),
        };
        setVenueData({
          ...venueData,
          contacts: [...venueData.contacts, normalizedContact],
        });
      }

      resetContactModal();
    } catch (error) {
      const errorMessage =
        (error as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Failed to create contact";
      toast.error(errorMessage);
    } finally {
      setSavingContact(false);
    }
  };

  const resetContactModal = () => {
    setOpenContactModal(false);
    setContactModalStep("search");
    setContactSearch("");
    setSearchResults([]);
    setSearchAttempted(false);
    setNewContactForm({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      role: "",
    });
  };

  return {
    openContactModal,
    setOpenContactModal,
    contactSearch,
    setContactSearch,
    searchResults,
    isSearching,
    savingContact,
    contactModalStep,
    setContactModalStep,
    searchAttempted,
    newContactForm,
    setNewContactForm,
    handleAddExistingContact,
    handleAddNewContact,
    resetContactModal,
  };
};

export default useContactSearch;
